import { useCallback, useEffect, useRef, useState } from "react";
import { getRuntimeApiBase } from "../lib/runtime-api-base";
import type { MarketAwarenessDossier } from "../types/market-awareness";

const API_BASE = getRuntimeApiBase();
const POLL_MS = 30_000;

function endpoint(instrument: string) {
  return `${API_BASE}/api/market-data/awareness/${encodeURIComponent(instrument)}?refresh=1`;
}

export function useMarketAwarenessDossier(
  instrument: string,
  options: {
    enabled?: boolean;
    initialDossier?: MarketAwarenessDossier | null;
  } = {},
) {
  const enabled = options.enabled ?? true;
  const initialDossier = options.initialDossier ?? null;
  const [dossier, setDossier] = useState<MarketAwarenessDossier | null>(
    initialDossier,
  );
  const [isLoading, setIsLoading] = useState(!initialDossier);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const instrumentRef = useRef(instrument);
  const dossierRef = useRef<MarketAwarenessDossier | null>(initialDossier);
  instrumentRef.current = instrument;

  const refresh = useCallback(async () => {
    try {
      setIsLoading((current) => current || dossierRef.current == null);
      const response = await fetch(endpoint(instrumentRef.current), {
        credentials: "include",
      });
      if (!mountedRef.current) return;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const next = (await response.json()) as MarketAwarenessDossier;
      dossierRef.current = next;
      setDossier(next);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(
        err instanceof Error ? err.message : "Market awareness fetch failed",
      );
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      dossierRef.current = null;
      setDossier(null);
      setIsLoading(false);
      setError(null);
      return () => {
        mountedRef.current = false;
      };
    }
    dossierRef.current = initialDossier;
    setIsLoading(!initialDossier);
    setDossier(initialDossier);
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [instrument, refresh, enabled, initialDossier]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [refresh, enabled]);

  return { dossier, refresh, isLoading, error };
}
